/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

/*
  This class wraps the Overlay Mission Button
 */
import React from 'react';
import {Button, IconButton} from '@mui/material';
import {Close, Edit, Flag, Visibility, VisibilityOff} from '@mui/icons-material';
import {Feature} from '@turf/helpers';
import isNil from 'lodash/isNil';
import size from 'lodash/size';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import styled, {withTheme} from 'styled-components';
import FlightLeg from '../../../../shared/entities/flightLeg';
import MissionPlan from '../../../../shared/entities/missionPlan';
import {TerminalPlatformsEnum} from '../../../../shared/entities/terminalPlatform';
import {Authorities} from '../../../../shared/entities/user';
import {
  selectFlightLegs,
  selectFlightLegServiceAlerts,
  selectFlightPathBeamsChecked,
  selectIsRequestingTailDetailsPanelTerminal,
  selectMissionPlansObj,
  selectOverlaidFlightLegHidden,
  selectOverlayMissionFlightLegListVisible,
  selectRequestTailDetailsPanelTerminalError,
  selectRouterPathname,
  selectSelectedOverlaidFlightLegId,
  selectselectedOverlaidMissionPlanId,
  selectTerminalDetails,
  useAppSelector,
} from '../../../../store';
import {
  hideMissionOverlayFlightLegListInModal,
  hideMissionOverlayMissionListInModal,
  hideMissionOverlayMissionListModal,
  showMissionOverlayFlightLegListInModal,
  showMissionOverlayMissionListInModal,
  showMissionOverlayMissionListModal,
} from '../../../../store/ui/actions';
import {
  addBeamToMap,
  clearMission,
  FTV_MAP_ID,
  removeBeamFromMap,
  toggleOverlaidFlightLegVisibility
} from '../../../../store/maps/actions';
import {clearMissionData} from '../../../../store/flightLegs/actions';
import {FEATURE_FLAGS, requireFeatureFlag} from '../../../../utils/FeatureFlagUtils';
import {isPlatformTypeInList} from '../../../../utils/PlatformTypeUtils';
import {requireRBACRoles} from '../../../../utils/RBACUtils';
import {EMPTY_FIELD_STRING_PLACEHOLDER} from '../../../../utils/StringUtils';
import StyledProps from '../../../common/theme/AppThemeInterface';
import appTheme from '../../../common/theme/appTheme.json';

const StyledButtonContainer = styled.div`
  position: absolute;
  top: 14px;
  left: 14px;
`;
const StyledButton = {
  paddingRight: '8px',
  backgroundColor: appTheme.secondaryBackgroundColor,
  borderRadius: '0px',
  color: appTheme.primaryButtonTextColor,
  letterSpacing: '0.14px',
  fontSize: '16px',
  lineHeight: '24px',
  ':hover': {
    backgroundColor: appTheme.secondaryBackgroundColor,
  },
};

const StyledIconButton = styled(IconButton)`
  background-color: ${({ theme }: StyledProps) => theme.secondaryBackgroundColor};
  height: 36px;
  border-radius: 0;
  color: ${({ theme }: StyledProps) => theme.primaryButtonTextColor};
  padding-left: 8px;
  padding-right: 8px;
  :hover {
    background-color: ${({ theme }: StyledProps) => theme.secondaryBackgroundColor};
  }
  .button__icon {
    margin-top: -3px;
    width: 20px;
    height: 20px;
  }
`;

const StyledIconButtonDisabled = styled(StyledIconButton)`
  cursor: default;
`;

export interface OverlayMissionPlanButtonContainerProps {}

const OverlayMissionPlanButtonContainer: React.FunctionComponent<OverlayMissionPlanButtonContainerProps> = (
  props: OverlayMissionPlanButtonContainerProps,
) => {
  const dispatch = useDispatch();

  const isRequestingTailDetailsPanelTerminal = useSelector(selectIsRequestingTailDetailsPanelTerminal, shallowEqual);
  const requestTailDetailsPanelTerminalError = useSelector(selectRequestTailDetailsPanelTerminalError, shallowEqual);
  const missionPlans = useAppSelector(selectMissionPlansObj, shallowEqual);
  const selectedOverlaidMissionPlanId = useSelector(selectselectedOverlaidMissionPlanId(FTV_MAP_ID), shallowEqual);
  const flightLegs = useSelector(selectFlightLegs, shallowEqual);
  const flightLegServiceAlerts = useSelector(selectFlightLegServiceAlerts, shallowEqual);
  const selectedOverlaidFlightLegId = useSelector(selectSelectedOverlaidFlightLegId(FTV_MAP_ID), shallowEqual);
  const overlaidFlightLegHidden = useSelector(selectOverlaidFlightLegHidden(FTV_MAP_ID), shallowEqual);
  const routerPathname = useSelector(selectRouterPathname, shallowEqual);
  const flightPathBeamsChecked = useSelector(selectFlightPathBeamsChecked(FTV_MAP_ID), shallowEqual);
  const fullTailViewTerminal = useSelector(selectTerminalDetails, shallowEqual);
  const overlayMissionFlightLegListVisible = useSelector(selectOverlayMissionFlightLegListVisible, shallowEqual);

  const handleOverlayMissionButtonClicked = () => {
    dispatch(showMissionOverlayMissionListModal());
    dispatch(showMissionOverlayMissionListInModal());
    dispatch(hideMissionOverlayFlightLegListInModal());
  };
  const handleEditFlightLegClicked = () => {
    dispatch(showMissionOverlayMissionListModal());
    dispatch(showMissionOverlayFlightLegListInModal());
    dispatch(hideMissionOverlayMissionListInModal());
  };

  const handleRemoveMissionOverlayClicked = () => {
    dispatch(hideMissionOverlayMissionListModal());
    dispatch(clearMission(FTV_MAP_ID)());
    dispatch(clearMissionData(FTV_MAP_ID)());
    // If the user sets any other beams visible we assume that when they clear the flight leg they will be keeping the other beams that were set
    // else if the flight leg beams are set we remove them from the map
    if (
      !isNil(selectedOverlaidFlightLegId) &&
      !isNil(flightLegServiceAlerts) &&
      !isNil(flightLegServiceAlerts[selectedOverlaidFlightLegId]) &&
      size(flightLegServiceAlerts[selectedOverlaidFlightLegId].beams) &&
      flightPathBeamsChecked
    ) {
      flightLegServiceAlerts[selectedOverlaidFlightLegId].beams.features.forEach((feature: Feature) => {
        dispatch(removeBeamFromMap(FTV_MAP_ID)(
          feature.properties?.name, 
          feature.properties?.network, 
          feature.properties?.mapType));
      });
    }
  };
  const handleToggleFlightLegVisibilityClicked = () => {
    // check to remove or add the beams to the map
    if (
      !isNil(selectedOverlaidFlightLegId) &&
      !isNil(flightLegServiceAlerts) &&
      !isNil(flightLegServiceAlerts[selectedOverlaidFlightLegId]) &&
      size(flightLegServiceAlerts[selectedOverlaidFlightLegId].beams) &&
      flightPathBeamsChecked
    ) {
      flightLegServiceAlerts[selectedOverlaidFlightLegId].beams.features.forEach((feature: Feature) => {
        if (overlaidFlightLegHidden) {
          dispatch(addBeamToMap(FTV_MAP_ID)(
            feature.properties?.name, 
            feature.properties?.network, 
            feature.properties?.mapType));
        } else {
          dispatch(removeBeamFromMap(FTV_MAP_ID)(
            feature.properties?.name, 
            feature.properties?.network, 
            feature.properties?.mapType
          ));
        }
      });
    }
    dispatch(toggleOverlaidFlightLegVisibility(FTV_MAP_ID)());
  };

  const pathname = routerPathname.substr(1, routerPathname.length);
  const isExportView = pathname.includes('/export');
  const restrictionEnabled = fullTailViewTerminal && fullTailViewTerminal.restrictionEnabled;
  const hideOverlayMissionBtnByPlatformType =
    fullTailViewTerminal &&
    isPlatformTypeInList(fullTailViewTerminal.platformType, TerminalPlatformsEnum.GroundNomadic);

  return !isRequestingTailDetailsPanelTerminal &&
    !requestTailDetailsPanelTerminalError &&
    !isExportView &&
    !restrictionEnabled &&
    !hideOverlayMissionBtnByPlatformType ? (
    <StyledButtonContainer id="missionPlanOverlayButton" data-test="full-tail-view__mission-overlay-button">
      <React.Fragment>
        {overlayMissionFlightLegListVisible ? (
          <StyledIconButtonDisabled disableRipple disableTouchRipple>
            <Flag className="button__icon" />
          </StyledIconButtonDisabled>
        ) : (
          <StyledIconButton>
            <Flag className="button__icon" />
          </StyledIconButton>
        )}
      </React.Fragment>
      <React.Fragment>
        {!isNil(selectedOverlaidMissionPlanId) && !isNil(selectedOverlaidFlightLegId) ? (
          <React.Fragment>
            <Button sx={{ ...StyledButton, cursor: 'default' }} disableRipple disableTouchRipple>
              {getButtonText(missionPlans, selectedOverlaidMissionPlanId, flightLegs, selectedOverlaidFlightLegId)}
            </Button>
            <StyledIconButton id="toggleFlightLegVisibilityButton" onClick={handleToggleFlightLegVisibilityClicked}>
              {/* TODO: hook up visibility */}
              {overlaidFlightLegHidden ? (
                <VisibilityOff className="button__icon" />
              ) : (
                <Visibility className="button__icon" />
              )}
            </StyledIconButton>
            {!isExportView && (
              <StyledIconButton id="editFlightLegButton" onClick={isExportView ? () => {} : handleEditFlightLegClicked}>
                <Edit className="button__icon" />
              </StyledIconButton>
            )}

            <StyledIconButton id="removeMissionOverlayButton" onClick={handleRemoveMissionOverlayClicked}>
              <Close className="button__icon" />
            </StyledIconButton>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <Button
              color="primary"
              sx={{ ...StyledButton }}
              id="overlayMissionButtonNonExport"
              onClick={isExportView ? () => {} : handleOverlayMissionButtonClicked}
            >
              Overlay Mission
            </Button>
            <StyledIconButton id="overlayMissionButton" onClick={handleOverlayMissionButtonClicked} />
          </React.Fragment>
        )}
      </React.Fragment>
    </StyledButtonContainer>
  ) : null;
};

export default withTheme(
  requireFeatureFlag(
    requireRBACRoles(
      OverlayMissionPlanButtonContainer,
      Authorities.ROLE_MISSION_PLANS_VIEW
    ),
    FEATURE_FLAGS.isMptEnabled,
  ),
);

const getButtonText = (
  missionPlans: { [key: string]: MissionPlan },
  selectedOverlaidMissionPlanId: number | undefined,
  flightLegs: { [key: string]: FlightLeg },
  selectedOverlaidFlightLegId: number | undefined,
) => {
  return `${
    size(missionPlans) && !isNil(selectedOverlaidMissionPlanId)
      ? missionPlans[selectedOverlaidMissionPlanId].name
      : EMPTY_FIELD_STRING_PLACEHOLDER
  }
  ${' - '}
  ${
    size(flightLegs) && !isNil(selectedOverlaidFlightLegId) && !isNil(flightLegs[selectedOverlaidFlightLegId])
      ? flightLegs[selectedOverlaidFlightLegId].origin
      : EMPTY_FIELD_STRING_PLACEHOLDER
  }
  ${' to '}
  ${
    size(flightLegs) && !isNil(selectedOverlaidFlightLegId) && !isNil(flightLegs[selectedOverlaidFlightLegId])
      ? flightLegs[selectedOverlaidFlightLegId].destination
      : EMPTY_FIELD_STRING_PLACEHOLDER
  }`;
};
